import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { Circle, FloatingWhiteWrapper, FormButton, List, ListItem, ListTitle, ListWrapper, ProfileWrapper } from './profile.styled'
import { Button, ConfigProvider, Form, Input, Modal, message } from 'antd'
import { modifyUser } from '../../apis/user'
import { adminData, checkUserToken, logout, modifyPassword } from '../../apis/auth'
import { setAdmin } from '../../store/adminSlice'
import { setUser, setLogout } from '../../store/userSlice'
import { useNavigate } from 'react-router-dom'

const passwordRule = /^(?=.{8,})(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9]).*$/
function Profile (props) {
  const { adminMode } = props
  const { t: ln_t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useSelector(state => state.user)
  const admin = useSelector(state => state.admin)
  const [form] = Form.useForm()
  const groupAfterFilterDuplicate = [...new Map(user.groups.map(item => [item.id, item])).values()]

  const [editName, setEditName] = useState(false)
  const [editPass, setEditPass] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const defPlaceholder = (name, type) => {
    const message = type === 'input' ? ln_t('util.pleaseEnter') : type === 'select' ? ln_t('util.pelaseSelect') : ''
    return `${message} ${ln_t(`profile.${name}`)}`
  }
  const defConfig = (name, type, required = true) => ({ name, label: ln_t(`profile.${name}`), rules: [{ required, message: defPlaceholder(name, type) }] })

  const handleModalClose = () => {
    setEditName(false)
    setEditPass(false)
    form.resetFields()
  }

  const checkAuth = async () => {
    const result = adminMode ? await adminData() : await checkUserToken()
    if (result?.status === 200) {
      const { name } = result.data
      dispatch(adminMode ? setAdmin({ ...admin, name }) : setUser({ ...user, name }))
    }
  }

  const handleModName = async value => {
    setIsLoading(true)
    const result = await modifyUser(user?.id, value)
    if (result.status === 200) {
      message.success(ln_t('message.success.modify'))
      checkAuth()
    }
    setIsLoading(false)
    handleModalClose()
  }

  const sendLogout = async () => {
    const result = await logout()
    if (result.status === 200) {
      dispatch(setLogout())
      navigate('/login')
    }
  }
  const handleModPass = async ({ oldPassword, confirmPassword }) => {
    const postData = {
      originPassword: window.btoa(oldPassword),
      password: window.btoa(confirmPassword)
    }
    const result = await modifyPassword(postData)
    if (result?.status === 200) Modal.info({ icon: false, content: ln_t('message.success.resetPass'), onOk: sendLogout })
  }

  const validation = {
    password: [{ required: true, message: defPlaceholder('newPassword', 'input') },
      { pattern: passwordRule, message: ln_t('message.error.matchPassPattern') }],
    confirmPassword: [{ required: true, message: defPlaceholder('confirmPassword', 'input') }]
  }

  return (
    <ConfigProvider theme={{ token: { colorPrimary: 'rgb(71, 182, 175)', colorPrimaryHover: 'rgba(71, 182, 175,.8)' } }}>
      <FloatingWhiteWrapper admin={adminMode}>
        <ProfileWrapper left={!adminMode} admin={adminMode}>
          <h3>{adminMode ? admin.name : user.name}</h3>
          <div style={{ marginBottom: '24px' }}>{adminMode ? admin.email : user.email}</div>
          <FormButton className="buttons">
            <Button type="default" onClick={() => { setEditName(true); form.setFieldsValue({ name: adminMode ? admin.name : user.name }) }}>{ln_t('profile.changeName')}</Button>
            <Button type="primary" onClick={() => setEditPass(true)}>{ln_t('profile.changePassword')}</Button>
          </FormButton>
        </ProfileWrapper>
        {adminMode
          ? ''
          : <ListWrapper style={{ display: 'flex' }}>
              <List left>
                <ListTitle brand>{ln_t('profile.brand')}</ListTitle>
                {user.brands?.map((item, index) => (
                  <ListItem key={`brand_${index}`}>
                    <Circle active={item.id === user.nowBrand} />{item.name}
                  </ListItem>
                ))}
              </List>
              <List>
                <ListTitle>{ln_t('profile.group')}</ListTitle>
                  {groupAfterFilterDuplicate?.map((item, index) => (
                    <ListItem key={`group_${index}`} className="profile-item">
                      <Circle active={item.id === user.nowGroup} />{item.name}
                    </ListItem>
                  ))}
              </List>
            </ListWrapper>
        }
      </FloatingWhiteWrapper>

      {/* Change Name Modal */}
      <Modal title={`${ln_t('util.edit')} ${ln_t('profile.name')}`} open={editName} footer={null} onCancel={handleModalClose}>
        <Form form={form} layout="vertical" requiredMark={false} onFinish={e => handleModName(e)}>
          <Form.Item {...defConfig('name', 'input')} style={{ marginTop: '16px' }}>
            <Input placeholder={defPlaceholder('name', 'input')} />
          </Form.Item>
          <FormButton style={{ justifyContent: 'center' }}>
            <Button type="default" onClick={handleModalClose}>{ln_t('util.cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>{ln_t('util.confirm')}</Button>
          </FormButton>
        </Form>
      </Modal>

      {/* Change Password Modal */}
      <Modal title={`${ln_t('util.edit')} ${ln_t('profile.password')}`} open={editPass} footer={null} onCancel={handleModalClose}>
        <Form form={form} layout="vertical" requiredMark={false} onFinish={e => handleModPass(e)} style={{ marginTop: '20px' }}>
          <Form.Item {...defConfig('oldPassword', 'input')}>
            <Input.Password placeholder={defPlaceholder('oldPassword', 'input')} />
          </Form.Item>
          <Form.Item {...defConfig('newPassword', 'input')}
            rules={[...validation.password, ({ getFieldValue }) => ({
              validator (rule, value) {
                if (!value || getFieldValue('oldPassword') !== value) return Promise.resolve()
                return Promise.reject(ln_t('message.error.newPassSameAsOld'))
              }
            })]}
          >
            <Input.Password placeholder={defPlaceholder('newPassword', 'input')}/>
          </Form.Item>
          <Form.Item {...defConfig('confirmPassword', 'input')}
              rules={[...validation.confirmPassword, ({ getFieldValue }) => ({
                validator (rule, value) {
                  if (!value || getFieldValue('newPassword') === value) return Promise.resolve()
                  return Promise.reject(ln_t('message.error.passIsNotSameWithNew'))
                }
              })]}
          >
            <Input.Password placeholder={defPlaceholder('confirmPassword', 'input')}/>
          </Form.Item>
          <div style={{ color: 'rgba(0,0,0,.4)', padding: '0px 4px' }}>
            <div>{ln_t('user.password.length')}</div>
            <div>{ln_t('user.password.format')}</div>
          </div>
          <FormButton style={{ justifyContent: 'center', marginTop: '16px' }}>
            <Button type="default" onClick={handleModalClose}>{ln_t('util.cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>{ln_t('util.confirm')}</Button>
          </FormButton>
        </Form>
      </Modal>
    </ConfigProvider>
  )
}

export default Profile
